import { InputLabel } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Icon from '@material-ui/core/Icon';
import Input from '@material-ui/core/Input';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import classNames from 'classnames';
import { isArray } from 'lodash';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import Select, {
    components,
    InputActionMeta,
    OptionProps,
    SelectComponentsConfig,
    StylesConfig,
    Theme,
    ValueType
} from 'react-select';
import AsyncSelect from 'react-select/async';
import { useAsync, UseAsyncStatus } from '../Hooks/useAsync';
import { CancelledRequest, HttpExecutorConfig, useHttpConfig } from '../Shared/HttpRequestExecutor';
import { Maybe, Nullable } from '../Shared/Types';
import { AutoCompleteTypes } from './MaskedTextField';
import { ReadOnlyField } from './ReadOnlyField';
import { StyledChip } from './StyledChip';
import styles from './StyledSelect.module.scss';

type SimpleModeConfig = {
    className: string;
};

export type SingleSelectProps<SuggestionType = any> = {
    label?: string;
    placeHolder?: string;
    readOnly?: boolean;
    helperText?: string;
    errorText?: string;
    warningText?: string;
    autoFocus?: boolean;
    required?: boolean;
    disabled?: boolean;
    maxResults: number;
    isMulti?: boolean;
    hideMultiChips?: boolean;
    allowMenuFloat?: boolean;
    noResultsMessage?: React.ReactNode;
    testId: string;
    simpleMode?: SimpleModeConfig;
    selectFirstAsyncResult?: boolean;
    hideWarningEmptyHelperText?: boolean;
    collapseTitleLabel?: boolean;
    itemToString(suggestion: SuggestionType): string;
    itemToMenuKey(suggestion: SuggestionType): string;
    itemHasError?(suggestion: SuggestionType): boolean;
    getMultiValueChipIcon?(suggestion: SuggestionType): React.ReactElement;
    customItemRender?(suggestion: Nullable<SuggestionType>): React.ReactNode;
    selectedCustomItemRender?(suggestion: Nullable<SuggestionType>): React.ReactNode;
    suggestionCustomItemRender?(suggestion: Nullable<SuggestionType>): React.ReactNode;
    onSelection?(item?: ValueType<SuggestionType | SuggestionType[]>): void;
    onOpen?(open: boolean): void;
    onChange?(value: string): void;
    onBlur?(e: React.FocusEvent): void;
    onClick?(e: React.MouseEvent): void;
    onClear?(): void;
};

function NoOptionsMessage(props: any) {
    if (props.selectProps.noResultsMessage)
        return React.cloneElement(props.selectProps.noResultsMessage, {
            ...props.innerProps
        });

    return (
        <Typography color='textSecondary' className={styles.noOptionsMessage} {...props.innerProps}>
            {props.children}
        </Typography>
    );
}

function inputComponent({ inputRef, ...props }: any) {
    return <div ref={inputRef} {...props} />;
}

function simpleInputComponent({ inputRef, ...props }: any) {
    return <div className={styles.simpleInput} ref={inputRef} {...props} />;
}

function Control(props: any) {
    let helperTextType: 'normal' | 'warning' | 'error' = 'normal';
    const unifiedHelperText: React.ReactNode =
        props.selectProps.errorText || props.selectProps.warningText || props.selectProps.helperText || '';

    if (props.selectProps.errorText) {
        helperTextType = 'error';
    } else if (props.selectProps.warningText) {
        helperTextType = 'warning';
    }

    const formControlClasses = classNames(styles.formControl, {
        [styles.errorState]: helperTextType === 'error'
    });

    const labelClass = !!props.disabled ? styles.disabledInputLabel : styles.inputLabel;
    const labelClasses = {
        root: labelClass
    };

    const inputClasses = {
        root: styles.inputRoot,
        focused: styles.inputFocused,
        input: styles.input
    };

    const formHelperTextClasses = {
        root: classNames(styles.helperText, {
            [styles.warning]: helperTextType === 'warning'
        })
    };

    return (
        <FormControl
            error={!!props.selectProps.errorText}
            disabled={!!props.selectProps.disabled}
            fullWidth
            classes={{ root: formControlClasses }}
            data-test-id={`single-select-input_${props.selectProps.testId}`}
        >
            {!props.selectProps.collapseTitleLabel && (
                <InputLabel shrink classes={labelClasses}>
                    {props.selectProps.label || ''}
                    {props.selectProps.required && <span className={styles.requiredLabel}> *</span>}
                </InputLabel>
            )}
            <Input
                inputProps={{
                    className: styles.input,
                    inputRef: props.innerRef,
                    children: props.children,
                    disabled: props.selectProps.disabled,
                    readOnly: props.selectProps.readOnly,
                    'data-lpignore': 'true',
                    ...props.innerProps
                }}
                data-test-id={`single-select-menu-input_${props.selectProps.testId}`}
                autoComplete={props.autoCompleteValue}
                disabled={props.selectProps.disabled}
                readOnly={props.selectProps.readOnly}
                placeholder={props.selectProps.placeholder}
                classes={inputClasses}
                inputComponent={inputComponent}
                className={styles.input}
                inputRef={props.innerRef}
            />
            {(!props.selectProps.hideWarningEmptyHelperText || unifiedHelperText) && (
                <FormHelperText classes={formHelperTextClasses}>{unifiedHelperText || ' '}</FormHelperText>
            )}
        </FormControl>
    );
}

function SimpleControl(props: any) {
    let helperTextType: 'normal' | 'warning' | 'error' = 'normal';
    const unifiedHelperText: React.ReactNode =
        props.selectProps.errorText || props.selectProps.warningText || props.selectProps.helperText;
    const inputClasses = {
        root: styles.inputRoot,
        input: styles.input
    };

    if (props.selectProps.errorText) {
        helperTextType = 'error';
    } else if (props.selectProps.warningText) {
        helperTextType = 'warning';
    }

    const formHelperTextClasses = {
        root: classNames(styles.helperText, {
            [styles.warning]: helperTextType === 'warning',
            [styles.error]: helperTextType === 'error'
        })
    };

    const autoCompleteValue: string = props.autoCompleteType === AutoCompleteTypes.UserInfo ? 'off-no-complete' : 'off';
    const simpleModeConfig = props.selectProps.simpleMode as SimpleModeConfig;

    return (
        <FormControl
            error={!!props.selectProps.errorText}
            disabled={!!props.selectProps.disabled}
            fullWidth
            data-test-id={`single-select-input_${props.selectProps.testId}`}
        >
            <Input
                inputProps={{
                    className: classNames(styles.input, simpleModeConfig.className),
                    inputRef: props.innerRef,
                    children: props.children,
                    readOnly: props.selectProps.readOnly,
                    ...props.innerProps
                }}
                autoComplete={autoCompleteValue}
                readOnly={props.selectProps.readOnly}
                placeholder={props.selectProps.placeholder}
                classes={inputClasses}
                inputComponent={simpleInputComponent}
                className={styles.inputSimple}
                disableUnderline
            />
            {(!props.selectProps.hideWarningEmptyHelperText || unifiedHelperText) && (
                <FormHelperText classes={formHelperTextClasses}>{unifiedHelperText || ' '}</FormHelperText>
            )}
        </FormControl>
    );
}

function Option(props: OptionProps<any>) {
    let contents: React.ReactNode = props.children;

    const index = props.options.findIndex(
        it => props.selectProps.itemToMenuKey(props.data) === props.selectProps.itemToMenuKey(it)
    );

    const dataTestId = `single-select-menu-item-${index}`;

    if (props.selectProps.suggestionCustomItemRender) {
        const suggestionCustomItemRenderValue = props.selectProps.suggestionCustomItemRender(props.data);
        contents = React.cloneElement(suggestionCustomItemRenderValue, {
            'data-test-id': dataTestId
        });
    } else if (props.selectProps.customItemRender) {
        const customItemRenderValue = props.selectProps.customItemRender(props.data);
        contents = React.cloneElement(customItemRenderValue, {
            'data-test-id': dataTestId
        });
    }
    return (
        <MenuItem
            buttonRef={props.innerRef}
            selected={props.isFocused}
            component='div'
            style={{
                fontWeight: props.isSelected ? 600 : 400,
                minHeight: 24,
                maxHeight: 65,
                height: 'auto',
                display: 'block',
                textOverflow: 'ellipsis'
            }}
            data-test-id={dataTestId}
            {...props.innerProps}
        >
            {contents}
        </MenuItem>
    );
}

function Placeholder(props: any) {
    return (
        <Typography color='textSecondary' className={styles.placeholder} {...props.innerProps}>
            {props.selectProps.placeHolder}
        </Typography>
    );
}

function SingleValue(props: any) {
    if (props.selectProps.selectedCustomItemRender) {
        const selectedItemCustomRenderValue = props.selectProps.selectedCustomItemRender(props.data);
        return React.cloneElement(selectedItemCustomRenderValue, { ...props.innerProps });
    } else if (props.selectProps.customItemRender) {
        const customItemRenderValue = props.selectProps.customItemRender(props.data);
        return React.cloneElement(customItemRenderValue, { ...props.innerProps });
    }

    return (
        <Typography className={styles.singleValue} {...props.innerProps}>
            {props.children}
        </Typography>
    );
}

function ValueContainer(props: any) {
    const classes = classNames(
        styles.valueContainer,
        props.selectProps && props.selectProps.simpleMode && styles.valueContainerFixedHeight
    );
    return <div className={classes}>{props.children}</div>;
}

function MultiValue(props: any) {
    const icon: JSX.Element = props.selectProps.getMultiValueChipIcon
        ? props.selectProps.getMultiValueChipIcon(props.data)
        : undefined;

    if (props.selectProps.hideMultiChips) {
        return null;
    } else {
        return (
            <StyledChip
                avatar={icon}
                key={props.data.id}
                tabIndex={-1}
                label={props.children}
                hasAccent
                maxLength={25}
                focused={props.isFocused}
                onRemove={props.removeProps.onClick}
                hasError={props.selectProps?.itemHasError ? props.selectProps?.itemHasError(props.data) : false}
                testId={`${props.selectProps.testId}-${props.data.id}`}
            />
        );
    }
}

function MenuList(props: any) {
    return (
        <div {...props.innerProps} className={styles.menuList} data-test-id={'single-select-menu'}>
            {props.children}
        </div>
    );
}

function Menu(props: any) {
    return (
        <div className={classNames({ [styles.hasMoreContainer]: props.selectProps.hasMoreResults })}>
            <components.Menu {...props}>
                <div className={styles.dropdownBody}>
                    {props.children}
                    {props.selectProps.hasMoreResults && (
                        <Typography className={styles.maxResults}>More results found - type to refine</Typography>
                    )}
                </div>
            </components.Menu>
        </div>
    );
}

function InputComponent(props: any) {
    return (
        <components.Input {...props} className={styles.forceInputFontFamily}>
            {props.children}
        </components.Input>
    );
}

function ClearIndicator(props: any) {
    const buttonClasses = {
        root: classNames(styles.clearButton, {
            [styles.clearButtonFocused]: props.isFocused
        })
    };

    return (
        <Button
            {...props.innerProps}
            size='small'
            classes={buttonClasses}
            data-test-id={`single-select-clear_${props.selectProps.testId}`}
            tabIndex={-1}
        >
            <Icon classes={{ root: styles.iconRoot }}>cancel</Icon>
        </Button>
    );
}

function DropdownIndicator(props: any) {
    return (
        <div
            {...props.innerProps}
            className={styles.arrowIcon}
            data-test-id={`single-select-drop-down_${props.selectProps.testId}`}
        >
            {props.selectProps.menuIsOpen ? <Icon>arrow_drop_up</Icon> : <Icon>arrow_drop_down</Icon>}
        </div>
    );
}

const loadingIndicator = (css: CSSProperties) => {
    css.position = 'absolute';
    css.right = 31;
    css.backgroundColor = 'transparent';

    return css;
};

const styleOverrides: StylesConfig = {
    loadingIndicator,
    menuPortal: base => ({ ...base, width: 'auto', minWidth: base.width + 'px', maxWidth: '600px', zIndex: 9999 })
};

const themeOverride = (theme: Theme): Theme => ({
    ...theme,
    borderRadius: 0,
    spacing: { ...theme.spacing, menuGutter: 1 }
});

function alwaysTrueFilterOptions() {
    return true;
}

export function isSingleSelectSingleValue<T>(item?: ValueType<T | T[]>): item is T {
    if (!!item) {
        if ((item as T[]).length === undefined && !isArray(item)) return true;
    }

    return false;
}

export function StyledSelect<SuggestionType>(
    props: SingleSelectProps<SuggestionType> & {
        value?: Nullable<SuggestionType> | SuggestionType[];
        useCache?: boolean;
        defaultValue?: Nullable<SuggestionType> | SuggestionType[];
        suggestions?: SuggestionType[];
        autoCompleteType?: AutoCompleteTypes;
        autoFocus?: boolean;
        isClearable: boolean;
        isSearchable?: boolean;
        selectRef?(inputElement: Nullable<AsyncSelect<SuggestionType> | Select<SuggestionType>>): void;
        fetch?(
            config: HttpExecutorConfig,
            inputValue: string,
            limit: number
        ): Promise<SuggestionType[] | CancelledRequest>;
        onFocus?(): void;
        onClick?(): void;
    }
) {
    const httpConfig = useHttpConfig();

    function getItemDisplayValue(value: SuggestionType): React.ReactNode {
        const icon: Maybe<React.ReactElement> = props.getMultiValueChipIcon
            ? props.getMultiValueChipIcon(value)
            : undefined;

        return props.customItemRender ? (
            props.customItemRender(value)
        ) : props.isMulti ? (
            <StyledChip
                avatar={icon}
                key={props.itemToMenuKey(value)}
                tabIndex={-1}
                label={props.itemToString(value)}
                hasAccent
                maxLength={25}
                readOnly
                testId={`chip-${props.itemToString(value)}`}
            />
        ) : (
            props.itemToString(value)
        );
    }

    const [menuOpen, setMenuOpen] = useState(false);
    const firstLoadRef = useRef(true);
    const firstLoad = firstLoadRef.current;

    const itemToString = props.itemToString;
    const itemToKey = !!props.itemToMenuKey ? props.itemToMenuKey : itemToString;

    const replacementComponents: SelectComponentsConfig<SuggestionType> = {
        Control: !!props.simpleMode ? SimpleControl : Control,
        Menu,
        MultiValue,
        NoOptionsMessage,
        Option,
        MenuList,
        Placeholder,
        SingleValue,
        ValueContainer,
        ClearIndicator,
        Input: InputComponent,
        DropdownIndicator: !!props.simpleMode ? undefined : DropdownIndicator
    };

    if (!props.fetch && !props.suggestions) console.error('Single Select has been given no fetch or suggestions');

    const errorAPICallback = () => Promise.reject('Error, no fetch function given to ASYNC select');

    const asyncFunction: (
        config: HttpExecutorConfig,
        inputValue: string,
        limit: number
    ) => Promise<SuggestionType[] | CancelledRequest> = props.fetch ? props.fetch : errorAPICallback;

    const queryAmount = props.maxResults > 0 ? props.maxResults + 1 : 0;
    const asyncFetchState = useAsync<SuggestionType[]>({
        defaultValue: [],
        httpConfig,
        useCache: props.useCache === undefined ? true : props.useCache,
        getApi: asyncFunction
    });

    useEffect(() => {
        async function fetchFirstResult() {
            if (props.selectFirstAsyncResult && props.fetch && props.onSelection) {
                const results = await props.fetch(httpConfig, '', 1);
                if (results instanceof CancelledRequest) return;

                firstLoadRef.current = false;
                if (results.length > 0) props.onSelection(results[0]);
            }
        }

        if (firstLoad && !props.value && props.fetch && props.selectFirstAsyncResult) {
            fetchFirstResult();
        }
    }, []);

    function onSelection(value: ValueType<SuggestionType>) {
        if (props.onSelection) props.onSelection(value);
    }

    if (props.readOnly) {
        const output = !props.value
            ? ''
            : !Array.isArray(props.value)
            ? getItemDisplayValue(props.value)
            : props.value.map(getItemDisplayValue);

        return (
            <ReadOnlyField label={props.label || null} mode='field'>
                {output}
            </ReadOnlyField>
        );
    }

    const selectProps = {
        ...props,
        ref: props.selectRef,
        styles: !!props.simpleMode ? undefined : styleOverrides,
        components: replacementComponents,
        getOptionLabel: itemToString,
        getOptionValue: itemToKey,
        value: props.value,
        isMulti: props.isMulti,
        isClearable: props.isClearable,
        hideSelectedOptions: false,
        onChange: onSelection,
        placeholder: props.placeHolder,
        isDisabled: props.disabled,
        theme: themeOverride,
        minMenuHeight: 241 // force a flip if less space than this available
    };

    if (props.fetch) {
        let results: SuggestionType[] = asyncFetchState.data || [];

        let hasMoreResults = false;
        if (props.maxResults > 0) {
            hasMoreResults = results.length > props.maxResults && asyncFetchState.status === UseAsyncStatus.Loaded;
            if (results.length > props.maxResults) {
                results = results.filter((x, i) => i < props.maxResults);
            }
        }

        const handleInputChange = (value: string, reason: InputActionMeta) => {
            if (reason.action === 'input-change') asyncFetchState.debouncedGetApi(value, queryAmount);
        };

        const handleMenuOpen = () => {
            if (!menuOpen) {
                setMenuOpen(true);
                asyncFetchState.getApi('', queryAmount);
            }
        };

        const handleMenuClose = () => {
            setMenuOpen(false);
        };

        return (
            <Select<SuggestionType>
                {...selectProps}
                filterOption={alwaysTrueFilterOptions}
                menuPortalTarget={document.getElementById('menu-portal')}
                hasMoreResults={hasMoreResults}
                menuPosition='fixed'
                menuPlacement='auto'
                onInputChange={handleInputChange}
                // don't open until loading finishes.  stay open for reloads
                menuIsOpen={menuOpen && asyncFetchState.status !== UseAsyncStatus.Loading}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                isLoading={
                    asyncFetchState.status === UseAsyncStatus.Reloading ||
                    asyncFetchState.status === UseAsyncStatus.Loading
                }
                options={results}
            />
        );
    }

    return (
        <Select<SuggestionType>
            {...selectProps}
            isSearchable={props.isSearchable}
            menuPortalTarget={document.getElementById('menu-portal')}
            menuPosition='fixed'
            menuPlacement='auto'
            options={props.suggestions || []}
        />
    );
}
